import { Link } from "react-router-dom";
import { Scale } from "lucide-react";
import PublicFooter from "@/components/PublicFooter";

export default function Terms() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="terms-brand-link">
            <Scale className="w-5 h-5 text-[#0B1B3D]" strokeWidth={1.75} />
            <span className="font-serif text-xl font-medium">FOIA Assist</span>
          </Link>
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500">Legal</div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="terms-page">
        <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-3">Terms of Service</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight font-medium mb-4">Terms of Service</h1>
        <div className="fa-badge fa-badge-warning mb-6" data-testid="terms-template-badge">
          Template — not legal advice. Please have your counsel review before public launch.
        </div>
        <p className="text-slate-600 leading-relaxed mb-8">
          Last updated: {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
        </p>

        <section className="space-y-6 text-slate-700 leading-relaxed">
          <div>
            <h2 className="font-serif text-2xl mb-2">1. Acceptance of Terms</h2>
            <p>By creating an account or otherwise accessing or using the FOIA Assist service (the &ldquo;Service&rdquo;), you agree to be bound by these Terms of Service and by our Privacy Policy. If you are entering into these Terms on behalf of an agency or other organization, you represent that you have the authority to bind that organization to these Terms.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">2. Service Description</h2>
            <p>FOIA Assist provides software tools to help U.S. public agencies intake, track, review, redact, and respond to public records requests. The Service includes AI-assisted features that surface suggested redactions and summaries. All AI outputs are recommendations that require human review and approval.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">3. Accounts and Trial</h2>
            <p>A 45-day free trial is offered for evaluation purposes. You are responsible for the security of your account credentials and for all activity that occurs under your account. You agree to notify us immediately of any unauthorized use.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">4. Fees and Billing</h2>
            <p>Paid plans are billed annually in advance either through the Stripe payment processor or through a purchase order (NET-30) workflow. If your plan includes case volume limits, additional cases beyond the included allowance are invoiced separately at the per-case rate stated on the pricing page. Fees are non-refundable except where required by law.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">5. Customer Data</h2>
            <p>You retain all rights in the documents, case data, and other content you upload to or generate in the Service (&ldquo;Customer Data&rdquo;). You grant us a limited license to process Customer Data solely to provide, secure, and improve the Service for you. We do not sell Customer Data.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">6. Acceptable Use</h2>
            <p>You agree not to (a) attempt to gain unauthorized access to the Service or other users&apos; data, (b) use the Service to violate any applicable law, or (c) upload content that infringes third-party rights or contains malware.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">7. AI Features Disclaimer</h2>
            <p>The Service&apos;s AI-assisted redaction and summary features are decision-support tools, not legal advice. All outputs must be reviewed by qualified agency personnel before any release to a requester. We make no warranty that AI outputs are complete or free of error.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">8. Warranties and Disclaimers</h2>
            <p>The Service is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. To the fullest extent permitted by law, we disclaim all warranties, express or implied, including fitness for a particular purpose and non-infringement.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">9. Limitation of Liability</h2>
            <p>To the fullest extent permitted by law, our aggregate liability under these Terms will not exceed the fees you paid us in the twelve months before the event giving rise to the claim. We will not be liable for indirect, incidental, consequential, or punitive damages.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">10. Termination</h2>
            <p>Either party may terminate these Terms at any time by providing written notice. Upon termination we will make Customer Data available for export for a reasonable period, after which we may delete it.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">11. Changes to These Terms</h2>
            <p>We may update these Terms from time to time. If we make material changes, we will provide notice through the Service or by email prior to the change taking effect.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">12. Contact</h2>
            <p>Questions about these Terms may be sent to the administrator email listed in your onboarding materials.</p>
          </div>
        </section>

        <div className="mt-10 pt-6 border-t border-slate-200 text-sm text-slate-500">
          <Link to="/privacy" className="underline decoration-[#C5A059] underline-offset-2" data-testid="terms-to-privacy-link">Read our Privacy Policy →</Link>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
